import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Space, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { ContentDistributionPeriodDetailOut } from '@/api/generated/schemas';
import { PublisherContractTag } from '@/components/content_catalog/distribution_periods/PublisherContractTag';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ShowLink } from '@/components/common/ShowLink';
import { productTypeColors } from '@/pages/content_catalog/products/constants';
import { formatDateTime } from '@/utils/format';
import dayjs from 'dayjs';

interface DistributionPeriodGeneralTabProps {
  record: ContentDistributionPeriodDetailOut;
}

export const DistributionPeriodGeneralTab = ({
  record,
}: DistributionPeriodGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const f = (key: string) =>
    t(`content_catalog.distribution_periods.fields.${key}`);
  const day = (value?: string | null) =>
    value ? dayjs(value).format('DD.MM.YYYY') : null;

  const items: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
    {
      key: 'product',
      label: f('product'),
      children: (
        <ShowLink resource="content_catalog.products" id={record.product_id}>
          {record.product_title ?? `#${record.product_id}`}
        </ShowLink>
      ),
    },
    {
      key: 'product_type',
      label: f('product_type'),
      children: orDash(
        record.product_type && (
          <Tag color={productTypeColors[record.product_type] || 'default'}>
            {t(`content_catalog.products.product_type.${record.product_type}`)}
          </Tag>
        )
      ),
    },
    {
      key: 'product_isbn',
      label: f('product_isbn'),
      children: orDash(
        record.product_isbn && (
          <Typography.Text code>{record.product_isbn}</Typography.Text>
        )
      ),
    },
    {
      key: 'publication',
      label: f('publication_id'),
      children: orDash(
        record.publication_title
          ? `${record.publication_title} (#${record.publication_id})`
          : record.publication_id
      ),
    },
    {
      key: 'publisher',
      label: f('publisher'),
      children: record.publisher_name ? (
        <Space>
          {record.publisher_id != null ? (
            <ShowLink
              resource="content_catalog.publishers"
              id={record.publisher_id}
            >
              {record.publisher_name}
            </ShowLink>
          ) : (
            record.publisher_name
          )}
          <PublisherContractTag
            hasActiveContract={record.publisher_has_active_contract}
          />
        </Space>
      ) : (
        '-'
      ),
    },
    {
      key: 'publisher_legal_name',
      label: f('publisher_legal_name'),
      children: orDash(record.publisher_legal_name),
    },
    {
      key: 'started_at',
      label: f('started_at'),
      children: orDash(day(record.started_at)),
    },
    {
      key: 'ended_at',
      label: f('ended_at'),
      children: orDash(day(record.ended_at)),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Card title={t('base.sections.basic_info')}>
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2, md: 3 }}
        items={items}
      />
    </Card>
  );
};
